// =============================================================================
// src/views/cinema/cinema-overlay-markup.ts — HTML do overlay do Modo Cinema.
//
// Proveniencia: assets/unified-app.js Sections.cinema._buildCinemaOverlays
//   (linhas 2158-2208). O Modo Cinema NAO tem markup estatico em unified.html: o
//   year-display, timeline, stats e controles sao gerados em runtime e inseridos
//   via insertAdjacentHTML('beforeend', ...) dentro de #mapOverlays. Aqui o HTML
//   e byte-fiel ao original (classes, ids uo-*/u-cinema-*, posicoes inline,
//   onclick sendPrompt) para paridade visual e de comportamento exatas.
//
// SRP: so a montagem da string HTML do overlay. Sem timers, sem mapa, sem DOM.
// =============================================================================

import type { ElectoralYear, CinemaYearFrame } from '../../app/types';
import { MANAUS_CINEMA_MARKER_POSITIONS } from '../../data';

/** Anos do Modo Cinema na ordem da timeline (Sections.cinema.years). */
export const CINEMA_YEARS: readonly ElectoralYear[] = [
  '2014', '2016', '2018', '2020', '2022', '2024',
];

/**
 * Ids dos overlays inseridos (Sections.cinema.overlays). A deactivate() remove
 * cada um por id, entao a lista precisa casar com os ids do HTML abaixo.
 */
export const CINEMA_OVERLAY_IDS: readonly string[] = [
  'uo-cinema-year', 'u-cinema-exit', 'u-cinema-shot', 'uo-cinema-ctx',
  'uo-ghost-ind', 'uo-cinema-stats-wrap', 'uo-cinema-timeline',
];

/**
 * Eventos historicos plotados na track (posicao %, titulo). Verbatim do legado
 * (Sections.cinema._buildCinemaOverlays, array `events`).
 */
const CINEMA_TIMELINE_EVENTS: readonly (readonly [pct: number, title: string])[] = [
  [15, '2015 · Lava Jato impacta MDB'],
  [26, '2016 · Impeachment Dilma'],
  [50, '2018 · Onda Bolsonaro'],
  [67, '2020 · Pandemia / Crise O2'],
  [83, '2022 · Lula presidente'],
];

/** HTML dos marcadores+rotulos de ano na timeline (loop `years.map` legado). */
function buildYearMarkersHtml(): string {
  return CINEMA_YEARS.map((year) => {
    /* v8 ignore next -- year ∈ CINEMA_YEARS, todos com posição não-zero em MANAUS_CINEMA_MARKER_POSITIONS; || 0 defensivo */
    const pos = MANAUS_CINEMA_MARKER_POSITIONS[year] || 0;
    const active = year === '2014' ? ' active' : '';
    return `<button class="u-cinema-marker${active}" data-year="${year}" style="left:${pos}%" title="${year}"></button>`
      + `<span class="u-cinema-label" style="left:${pos}%">${year}</span>`;
  }).join('');
}

/** HTML dos marcadores de evento historico na track (loop `events.map` legado). */
function buildTimelineEventsHtml(): string {
  return CINEMA_TIMELINE_EVENTS.map(
    (ev) => `<span class="u-cinema-event" style="left:${ev[0]}%" title="${ev[1]}"></span>`,
  ).join('');
}

/** Bloco year-display (numero/sub/mes) — topo esquerdo do overlay. */
function buildYearDisplayHtml(year: ElectoralYear, frame: CinemaYearFrame): string {
  return '<div class="u-cinema-overlay u-year-display" id="uo-cinema-year">'
    + `<div class="u-year-num" id="uo-cinema-year-num">${year}</div>`
    + `<div class="u-year-sub" id="uo-cinema-year-sub">${frame.sub}</div>`
    + `<div class="u-year-month" id="uo-cinema-year-month">${frame.month}</div>`
    + '</div>';
}

/** Botoes flutuantes (sair / capturar frame) e card de contexto historico. */
function buildGhostButtonsHtml(frame: CinemaYearFrame): string {
  return '<button class="u-cinema-overlay u-cinema-ghost" id="u-cinema-exit" style="top:80px;right:18px"><i class="ti ti-x" style="font-size:12px"></i> sair</button>'
    + '<button class="u-cinema-overlay u-cinema-ghost" id="u-cinema-shot" style="top:120px;right:18px" onclick="sendPrompt(\'Capturar este frame como snapshot\')"><i class="ti ti-camera" style="font-size:12px"></i> capturar frame</button>'
    + '<div class="u-cinema-overlay u-context-card" id="uo-cinema-ctx">'
    + '<div class="u-ctx-label"><i class="ti ti-bookmark" style="font-size:9px"></i> contexto histórico</div>'
    + `<div class="u-ctx-text" id="uo-cinema-ctx-text">${frame.context}</div>`
    + '</div>'
    + '<div class="u-cinema-overlay u-ghost-indicator" id="uo-ghost-ind"><i class="ti ti-ghost-2" style="font-size:12px"></i> sobreposição ano anterior</div>';
}

/** Card de distribuicao % de votos (stats) — preenchido depois por renderStats. */
function buildStatsWrapHtml(year: ElectoralYear): string {
  return '<div class="u-cinema-overlay u-cinema-stats" id="uo-cinema-stats-wrap">'
    + `<div class="sb-label">distribuição % de votos · Manaus · <span id="u-cinema-stats-year">${year}</span></div>`
    + '<div class="u-stats-stack" id="u-cinema-stats"></div>'
    + '</div>';
}

/** Timeline com controles (prev/play/next, ghost, velocidade) e track de anos/eventos. */
function buildTimelineHtml(): string {
  return '<div class="u-cinema-overlay u-cinema-timeline" id="uo-cinema-timeline">'
    + '<div class="u-cinema-control-row">'
    + '<button class="u-cinema-btn" id="u-cinema-prev" aria-label="anterior"><i class="ti ti-player-skip-back"></i></button>'
    + '<button class="u-cinema-btn main" id="u-cinema-play" aria-label="play"><i class="ti ti-player-play"></i></button>'
    + '<button class="u-cinema-btn" id="u-cinema-next" aria-label="próximo"><i class="ti ti-player-skip-forward"></i></button>'
    + '<span style="flex:1"></span>'
    + '<button class="u-cinema-ghost" id="u-cinema-ghost"><i class="ti ti-ghost-2" style="font-size:12px"></i> ghost mode</button>'
    + '<button class="u-cinema-speed active" data-speed="1">1x</button>'
    + '<button class="u-cinema-speed" data-speed="2">2x</button>'
    + '<button class="u-cinema-speed" data-speed="4">4x</button>'
    + '</div>'
    + `<div class="u-cinema-track"><div class="u-cinema-line"></div><div class="u-cinema-progress" id="u-cinema-progress" style="width:6%"></div>${buildTimelineEventsHtml()}${buildYearMarkersHtml()}</div>`
    + '</div>';
}

/**
 * Concatena o HTML completo do overlay do Modo Cinema para um ano inicial.
 * Ordem dos blocos identica ao legado (year-display -> botoes/contexto -> stats
 * -> timeline) para preservar empilhamento e seletores.
 */
export function buildCinemaOverlayHtml(year: ElectoralYear, frame: CinemaYearFrame): string {
  return buildYearDisplayHtml(year, frame)
    + buildGhostButtonsHtml(frame)
    + buildStatsWrapHtml(year)
    + buildTimelineHtml();
}
